/**
 * t264u: one rule for every comment box (task, issue, feed post, announcement, kudos, shift offer, project, thread
 * replies, and editing a comment): Enter sends (or saves), Shift+Enter is a new line, Esc cancels where there's a
 * cancel. Enter does nothing while an IME is composing (Japanese, Chinese, …) or while a mention / emoji popup is
 * open (that popup takes Enter to pick), and empty or whitespace-only text is never sent. Phones behave like the
 * chat composer: Return sends too.
 */
import type { KeyboardEvent } from 'react';

export interface EnterToSendOptions {
  /** Send (or save). Only called when the text has something other than whitespace. */
  onSend: () => void;
  /** The current text, to skip sending an empty one. */
  value: string;
  /** Esc: cancel the edit / clear the box. */
  onCancel?: () => void;
  /** A mention or emoji autocomplete is open, so Enter belongs to it. */
  popupOpen?: boolean;
}

/** Whether this keydown is a plain Enter that should send (no Shift, not mid-IME-composition). */
export function isSendEnter(e: Pick<KeyboardEvent, 'key' | 'shiftKey'> & { nativeEvent?: { isComposing?: boolean; keyCode?: number }; keyCode?: number }): boolean {
  if (e.key !== 'Enter' || e.shiftKey) return false;
  // Safari reports keyCode 229 for the Enter that ends a composition.
  return !(e.nativeEvent?.isComposing || e.nativeEvent?.keyCode === 229 || e.keyCode === 229);
}

export function enterToSend({ onSend, value, onCancel, popupOpen }: EnterToSendOptions) {
  return (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (popupOpen) return;
    if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); return; }
    if (!isSendEnter(e)) return;
    e.preventDefault();
    if (value.trim()) onSend();
  };
}

/** Hook form of enterToSend, for components that prefer `onKeyDown={useEnterToSend({...})}`. */
export const useEnterToSend = enterToSend;
